import React from 'react'
import PropTypes from 'prop-types'
import * as Styled from './GeneralLocation.styles'
import { Icon, CalculateRouteBtn } from 'components/atoms'
import { useMobileSwitch } from 'hooks'
import { Wegstrecke } from '@aok/icon-functional'
import { entryPointId } from 'settings/app/constants'
import { HospitalParkingInformation } from './HospitalParkingInformation/HospitalParkingInformation'

/**
 * simple address information
 * used for one address in locations
 * @param address
 * @param accessibility
 * @param areaServed
 * @param marker
 * @param searchLocationDistance
 * @returns {JSX.Element}
 * @constructor
 */
const GeneralLocation = ({
  address,
  accessibility,
  areaServed,
  marker,
  searchLocationDistance,
  entryPoint,
}) => {
  const iconSet = {
    distance: 'map-marker',
    nextStop: 'train',
    freeParking: 'parking',
  }

  const isMobile = useMobileSwitch()

  const checkAccessibility = (key, value) => {
    switch (key) {
      case 'distance':
        return (
          !!value &&
          // distance string example "Entfernung 0.00 km"
          // convert first part of the string to number
          Number(value.split(' ')[1]) > 0
        )
      case 'dedicatedParking':
      case 'parkingCostsPerHour':
      case 'parkingCostsPerDay':
        return false
      default:
        return !!value
    }
  }

  return (
    <Styled.GeneralLocation>
      <ul className='accessibility-list'>
        {Object.keys(accessibility)
          .filter(
            (k) =>
              !(entryPoint === entryPointId.HOSPITALS && k === 'freeParking')
          )
          .map((index) => {
            if (index === 'distance' && searchLocationDistance) {
              return (
                <li
                  key={ index }
                  className='accessibility-list-item'
                  data-cy='accessibility-list-item'
                  data-cy-accessibility-type='searchLocationDistance'
                  data-cy-accessibility-value={ searchLocationDistance }>
                  <span>
                    <Wegstrecke className='icon-grey' />
                  </span>
                  <span data-cy='detail-view-search-location-distance'>
                    {searchLocationDistance} km
                  </span>
                </li>
              )
            }

            return (
              checkAccessibility(index, accessibility[index]) && (
                <li
                  key={ index }
                  className='accessibility-list-item'
                  data-cy='accessibility-list-item'
                  data-cy-accessibility-type={ index }
                  data-cy-accessibility-value={ accessibility[index] }>
                  <Icon className={ `icon-f-${ iconSet[index] }` } />
                  <p>{accessibility[index]}</p>
                </li>
              )
            )
          })}

        {entryPoint === entryPointId.HOSPITALS && (
          <HospitalParkingInformation accessibility={ accessibility } />
        )}
      </ul>

      <div className='accessibility-address'>
        <span className='accessibility-address-title'> Adresse </span>
        <span>
          {' '}
          {address.street} {address.number}
        </span>
        <span>
          {' '}
          {address.zip} {address.city}
        </span>
      </div>

      {areaServed && (
        <div className='area-served'>
          <span className='area-served-title'>
            {' '}
            Vom Pflegedienst betreutes Gebiet{' '}
          </span>
          <span>{areaServed}</span>
        </div>
      )}

      {!isMobile && marker && (
        <CalculateRouteBtn
          marker={ marker }
          address={ address }
          navType='address'
          className='route-btn'
        />
      )}
    </Styled.GeneralLocation>
  )
}

GeneralLocation.propTypes = {
  /** address data **/
  address: PropTypes.shape({
    title: PropTypes.string,
    street: PropTypes.string.isRequired,
    zip: PropTypes.string.isRequired,
    city: PropTypes.string.isRequired,
  }).isRequired,
  /** accessibility data **/
  accessibility: PropTypes.shape({
    distance: PropTypes.string,
    nextStop: PropTypes.string,
    freeParking: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  }).isRequired,
  areaServed: PropTypes.string,
  /** used for google maps navigation (lon && lat) **/
  marker: PropTypes.shape({
    lat: PropTypes.number,
    lon: PropTypes.number,
  }),
  searchLocationDistance: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool,
  ]),
  entryPoint: PropTypes.string.isRequired,
}

export default GeneralLocation
